import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Image,
  ImageProps,
  Skeleton,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { convertMinutesToHours } from "@picovico/utils";
import { useLazyGetMovieDetailQuery } from "@store/services/movie";
import { format } from "date-fns";
import { useRouter } from "next/router";
import { lazy, Suspense, useEffect } from "react";
import { useAppSelector } from "store";

const ReactPlayer = lazy(() => import("react-player"));

export interface MovieDetailComponent1Props {
  containerProps?: BoxProps;
  cardProps?: BoxProps;
  videoColumnProps?: BoxProps;
  videoInnerProps?: BoxProps;
  posterColumnProps?: FlexProps;
  posterImageProps?: ImageProps;
  metaContainerProps?: BoxProps;
  metaRowProps?: FlexProps;
  metaLabelProps?: TextProps;
  metaValueProps?: TextProps;
  aboutSectionProps?: BoxProps;
  aboutTitleProps?: TextProps;
  aboutTextProps?: TextProps;
}

function MetaRow({
  label,
  value,
  metaRowProps,
  metaLabelProps,
  metaValueProps,
}: {
  label: string;
  value?: string | null;
  metaRowProps?: FlexProps;
  metaLabelProps?: TextProps;
  metaValueProps?: TextProps;
}) {
  if (!value) return null;
  return (
    <Flex gap={1} align="baseline" flexWrap="wrap" {...metaRowProps}>
      <Text flexShrink={0} {...metaLabelProps}>
        {label}:
      </Text>
      <Text {...metaValueProps}>{value}</Text>
    </Flex>
  );
}

export function MovieDetailComponent1({
  containerProps,
  cardProps,
  videoColumnProps,
  videoInnerProps,
  posterColumnProps,
  posterImageProps,
  metaContainerProps,
  metaRowProps,
  metaLabelProps,
  metaValueProps,
  aboutSectionProps,
  aboutTitleProps,
  aboutTextProps,
}: MovieDetailComponent1Props) {
  const router = useRouter();
  const movieIdx = router?.query?.movieIdx;
  const { company_idx } = useAppSelector((state) => state.config);

  const [getMovieDetail, { data: movieDetailRes }] = useLazyGetMovieDetailQuery(
    {},
  );
  const movie = movieDetailRes?.data;

  useEffect(() => {
    if (movieIdx) {
      getMovieDetail({ idx: movieIdx, company_idx });
    }
  }, [movieIdx, company_idx, getMovieDetail]);

  if (!movie) return null;

  const duration = movie.duration
    ? convertMinutesToHours(movie.duration)
    : undefined;
  const releaseDate = movie.release_date
    ? format(new Date(movie.release_date), "yyyy-MM-dd")
    : undefined;

  const metaRows: { label: string; value?: string | null }[] = [
    { label: "Name", value: movie.name },
    { label: "Release date", value: releaseDate },
    { label: "Cast", value: movie.cast },
    { label: "Director", value: movie.director },
    { label: "Duration", value: duration },
    { label: "Genre", value: movie.genre },
    { label: "Format", value: movie.roll_quality },
  ];

  return (
    <Box {...containerProps}>
      <Flex
        direction={{ base: "column", md: "row" }}
        align="stretch"
        overflow="hidden"
        {...cardProps}
      >
        <Box
          bg="black"
          w={{ base: "100%", md: "62%" }}
          flexShrink={0}
          position="relative"
          minH={{ base: "220px", md: "0" }}
          p={10}
          {...videoColumnProps}
        >
          {movie.trailer && (
            <Suspense
              fallback={
                <Skeleton
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                />
              }
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                {...videoInnerProps}
              >
                <ReactPlayer url={movie.trailer} width="100%" height="100%" />
              </Box>
            </Suspense>
          )}
        </Box>

        <Flex
          flex="1"
          minW="0"
          direction="column"
          overflow="hidden"
          {...posterColumnProps}
        >
          {movie.poster && (
            <Image
              src={movie.poster}
              alt={movie.name}
              w="100%"
              h={{ base: "200px", md: "280px" }}
              objectFit="cover"
              objectPosition="center top"
              display="block"
              flexShrink={0}
              {...posterImageProps}
            />
          )}

          <Box flexShrink={0} {...metaContainerProps}>
            {metaRows.map(({ label, value }) => (
              <MetaRow
                key={label}
                label={label}
                value={value}
                metaRowProps={metaRowProps}
                metaLabelProps={metaLabelProps}
                metaValueProps={metaValueProps}
              />
            ))}
          </Box>
        </Flex>
      </Flex>

      {movie.description && (
        <Box {...aboutSectionProps}>
          <Text {...aboutTitleProps}>About The Movie</Text>
          <Text {...aboutTextProps}>{movie.description}</Text>
        </Box>
      )}
    </Box>
  );
}

export default MovieDetailComponent1;
